'use client';

import { throttle } from 'es-toolkit';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { SELECTOR } from '@/constants';
import { useHistoryStore } from '@/stores/HistoryStore/useHistoryStore';
import { scrollHistoryTracker } from '@/utils/scrollHistoryTracker';

/**
 * * URL에서 서치파라미터를 제거한 pathname 반환
 */
const getPathnameWithoutSearchParams = (fullPath: string): string => {
  return fullPath.split('?')[0];
};

export const ScrollHistoryTracker = () => {
  const pathname = usePathname();
  const { set, get } = scrollHistoryTracker();
  const history = useHistoryStore((state) => state.customHistory);
  const removedRefreshHistory = getRemovedRefreshHistory(history);
  const recentHistory = [...removedRefreshHistory.values()].slice(-2);

  useEffect(() => {
    const mainElement = document.getElementById(SELECTOR.MAIN);
    if (!mainElement || !pathname) return;

    //* 이전 스크롤 위치 복원 (최근 히스토리에 현재 pathname이 포함된 경우)
    const previousScrollTop = get(pathname);
    const cleanPathnameHistory = recentHistory.map(
      getPathnameWithoutSearchParams,
    );
    const currentCleanPathname = getPathnameWithoutSearchParams(pathname);

    if (cleanPathnameHistory.includes(currentCleanPathname)) {
      mainElement.scrollTo({ top: previousScrollTop });
    }

    //* 스크롤 중 위치 저장 (throttle)
    const scrollHandler = throttle(() => {
      set(pathname, mainElement.scrollTop);
    }, 150);

    //* 스크롤 끝날 때 최종 위치 저장
    const scrollEndHandler = () => {
      set(pathname, mainElement.scrollTop);
    };

    mainElement.addEventListener('scroll', scrollHandler);
    mainElement.addEventListener('scrollend', scrollEndHandler);

    return () => {
      mainElement.removeEventListener('scroll', scrollHandler);
      mainElement.removeEventListener('scrollend', scrollEndHandler);
    };
  }, [pathname, set, get, recentHistory]);

  return null;
};

/**
 * * 새로고침으로 인해 연속으로 중복된 히스토리를 제거한 히스토리 반환
 */
const getRemovedRefreshHistory = (
  history: Map<number, string>,
): Map<number, string> => {
  const removedRefreshHistory = new Map<number, string>();
  const entries = Array.from(history.entries());

  if (entries.length === 0) return removedRefreshHistory;

  removedRefreshHistory.set(entries[0][0], entries[0][1]);

  for (let i = 1; i < entries.length; i++) {
    const currentValue = entries[i][1];
    const previousValue = entries[i - 1][1];

    const currentCleanPath = getPathnameWithoutSearchParams(currentValue);
    const previousCleanPath = getPathnameWithoutSearchParams(previousValue);

    if (currentCleanPath !== previousCleanPath) {
      removedRefreshHistory.set(entries[i][0], currentValue);
    }
  }

  return removedRefreshHistory;
};
